export const HYDRATION_IMPORT_LIMIT = 2;

type QueueTask = {
  priority: number;
  run: () => Promise<void>;
};

type SchedulerWithYield = {
  yield?: () => Promise<void>;
};

/**
 * Ends the current task so the following import does not extend a long task.
 * Uses `scheduler.yield` when the browser provides it.
 *
 * @returns Promise that resolves on the next turn of the event loop.
 */
export function yieldToMain(): Promise<void> {
  const scheduler = (globalThis as { scheduler?: SchedulerWithYield }).scheduler;
  if (typeof scheduler?.yield === 'function') {
    return scheduler.yield();
  }
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

/**
 * Runs hydration tasks with a fixed number of in-flight imports.
 * A later enqueue with a smaller priority waits ahead of tasks that have not started.
 *
 * @param limit - Maximum number of `run` calls in flight. Listing starts at 2.
 * @param yieldFn - Called after each task so the next import starts in a new task.
 * @returns Enqueue function. Tasks start immediately while a slot is free.
 */
export function createHydrationQueue(
  limit: number,
  yieldFn: () => Promise<void> = yieldToMain,
) {
  const pending: QueueTask[] = [];
  let active = 0;

  function pump() {
    while (active < limit && pending.length > 0) {
      const task = pending.shift();
      if (!task) {
        return;
      }
      active += 1;
      void task.run()
        .then(() => yieldFn())
        .finally(() => {
          active -= 1;
          pump();
        });
    }
  }

  return {
    /**
     * Inserts a task by priority and starts it when a slot is free.
     *
     * @param task - Priority (lower first) and the import-and-hydrate work.
     */
    enqueue(task: QueueTask) {
      const index = pending.findIndex((item) => item.priority > task.priority);
      if (index === -1) {
        pending.push(task);
      } else {
        pending.splice(index, 0, task);
      }
      pump();
    },
  };
}
